
import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Lock, Info } from "lucide-react";

interface CreditCardFormProps {
  onSubmit: (e: React.FormEvent) => void;
  isProcessing: boolean;
  amount: string;
}

const CreditCardForm = ({ onSubmit, isProcessing, amount }: CreditCardFormProps) => {
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [name, setName] = useState("");

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || "";
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(" ");
    } else {
      return value;
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Cardholder Name</Label>
        <Input id="name" placeholder="John Doe" value={name} onChange={e => setName(e.target.value)} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="cardNumber">Card Number</Label>
        <div className="relative">
          <Input
            id="cardNumber"
            placeholder="4242 4242 4242 4242"
            value={cardNumber}
            onChange={e => setCardNumber(formatCardNumber(e.target.value))}
            maxLength={19}
          />
          <CreditCard className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="expiry">Expiry Date</Label>
          <Input
            id="expiry"
            placeholder="MM/YY"
            value={expiryDate}
            onChange={e => setExpiryDate(e.target.value)}
            maxLength={5}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cvv" className="flex items-center gap-1">
            CVV
            <Info className="h-4 w-4 text-gray-400 cursor-help" />
          </Label>
          <Input
            id="cvv"
            placeholder="123"
            value={cvv}
            onChange={e => setCvv(e.target.value)}
            maxLength={4}
          />
        </div>
      </div>

      <Button type="submit" className="w-full bg-[#007BFF] hover:bg-[#0056b3] text-white py-6" disabled={isProcessing}>
        {isProcessing ? (
          <div className="flex items-center justify-center gap-2">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{
                duration: 1,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <Lock className="h-4 w-4" />
            </motion.div>
            Processing...
          </div>
        ) : (
          <>Pay ${amount} and Activate Strategy</>
        )}
      </Button>
    </form>
  );
};

export default CreditCardForm;
